import { ChangeEvent, useContext } from 'react';
import { useSelector } from 'react-redux';

import CreateEditSchemaModalContext from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModalContext';
import { RadioGroup, Select, TextField } from '@atom/mui';
import { budgetUnitsSelector } from '@atom/selectors/preferencesSelectors';
import { SchemaBudgetType } from '@atom/types/schema';

import LabelWithDescription from './LabelWithDescription';

import classes from './createEditSchemaModal.module.css';

const { MenuItem } = Select;
const { RadioButton } = RadioGroup;

const styles = {
  unitOption: {
    textTransform: 'uppercase',
  },
};

const CostAndUnitTab = () => {
  const { costType, setCostType, rate, setRate, unit, setUnit } = useContext(
    CreateEditSchemaModalContext,
  );

  const budgetUnits = useSelector(budgetUnitsSelector);
  const unitOptions = ['', ...budgetUnits];

  const updateRate = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRate(value ? Math.abs(Number(value)) : null);
  };

  const showUnit = costType === SchemaBudgetType.VARIABLE_COST;
  const showCostAndUnit = costType === SchemaBudgetType.FIXED;

  return (
    <div className={classes.tabContainer}>
      <div className={classes.tabTitle}>Cost &amp; Unit</div>
      <RadioGroup
        value={costType}
        onChange={(_, value) => setCostType(value as SchemaBudgetType)}
        sx={{ gap: '1rem' }}
      >
        <RadioButton
          value={SchemaBudgetType.VARIABLE}
          label={
            <LabelWithDescription
              title="Variable"
              description="Set the cost and unit at the individual inventory item level."
            />
          }
        />
        <RadioButton
          value={SchemaBudgetType.VARIABLE_COST}
          label={
            <LabelWithDescription
              title="Variable Cost"
              description="Set a fixed unit for all inventory of this type."
            />
          }
        />
        {showUnit && (
          <div className={classes.unitRow}>
            <Select
              label="Unit"
              fullWidth
              InputProps={{ style: styles.unitOption }}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUnit(event.target.value)
              }
              value={unit}
            >
              {unitOptions.map(option => (
                <MenuItem
                  key={option}
                  value={option || null}
                  style={styles.unitOption}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        <RadioButton
          value={SchemaBudgetType.FIXED}
          label={
            <LabelWithDescription
              title="Fixed"
              description="Set a fixed cost and unit for all inventory of this type."
            />
          }
        />
        {showCostAndUnit && (
          <div className={classes.costUnitRow}>
            <TextField
              label="Cost"
              value={rate ?? ''}
              type="number"
              fullWidth
              onChange={updateRate}
              slotProps={{ input: { startAdornment: '$' } }}
            />
            <div className={classes.perSeparator}>per</div>
            <Select
              label="Unit"
              fullWidth
              InputProps={{ style: styles.unitOption }}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUnit(event.target.value)
              }
              value={unit}
            >
              {unitOptions.map(option => (
                <MenuItem
                  key={option}
                  value={option || null}
                  style={styles.unitOption}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
      </RadioGroup>
    </div>
  );
};

export default CostAndUnitTab;
